import React, { useState } from 'react';

import { CostTypeRef } from '@leedpulse-monorepo/enums';
import { Button, InputNumber, Popconfirm, Select, Table } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

type LeedbucksRatesMap = Partial<Record<CostTypeRef, { unit_value: number }>>;

interface LeedbucksRatesProps {
    rates: LeedbucksRatesMap;
    onChange?: (rates: LeedbucksRatesMap) => void;
    isReadOnly?: boolean;
    canManageRows?: boolean; // shows the add row and the delete buttons
}

interface Row {
    key: string;
    value: number | null;
}

const ADD_ROW_KEY = 'add';

export default function LeedbucksRatesList({ rates, onChange, isReadOnly = false, canManageRows = false }: LeedbucksRatesProps) {
    const [newType, setNewType] = useState<CostTypeRef | null>(null);

    const bManageRows = canManageRows && !isReadOnly;

    const addRate = () => {
        if (!newType) return;
        onChange?.({ ...rates, [newType]: { unit_value: 0 } });
        setNewType(null);
    };

    const removeRate = (type: string) =>
        onChange?.(Object.fromEntries(Object.entries(rates).filter(([key]) => key !== type)) as LeedbucksRatesMap);

    const rows: Row[] = Object.entries(rates).map(([key, value]) => ({
        key,
        value: value.unit_value,
    }));

    return <>
        <Table<Row>
            pagination={false}
            scroll={{ y: 520 }}
            columns={[
                {
                    title: 'Type de coût',
                    dataIndex: 'key',
                    key: 'key',
                    render: (key: string) => key === ADD_ROW_KEY
                        ? <Select
                            showSearch
                            size="small"
                            style={{ width: '100%' }}
                            placeholder="Ajouter un type de coût"
                            value={newType}
                            onChange={setNewType}
                            options={Object.values(CostTypeRef)
                                .filter((type) => rates[type] === undefined)
                                .map((type) => ({ label: type, value: type }))}
                        />
                        : key,
                },
                {
                    title: 'Consommation de Leedbucks par unité',
                    dataIndex: 'value',
                    key: 'value',
                    render: (value: number | null, record) => record.key === ADD_ROW_KEY
                        ? <Button type="primary" size="small" icon={<PlusOutlined />} disabled={!newType} onClick={addRate} />
                        : <InputNumber
                            disabled={isReadOnly}
                            min={0}
                            max={999}
                            step={1}
                            precision={0}
                            placeholder="-"
                            value={value}
                            size="small"
                            onChange={(next) => onChange?.({ ...rates, [record.key]: { unit_value: next ?? 0 } })}
                        />,
                },
                ...(bManageRows ? [{
                    key: 'delete',
                    width: 56,
                    render: (_: unknown, record: Row) => record.key === ADD_ROW_KEY ? null : <Popconfirm
                        title="Supprimer ce taux ?"
                        okText="Supprimer"
                        cancelText="Annuler"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => removeRate(record.key)}
                    >
                        <Button danger size="small" icon={<DeleteOutlined />} />
                    </Popconfirm>,
                }] : []),
            ]}
            dataSource={bManageRows ? [...rows, { key: ADD_ROW_KEY, value: null }] : rows}
        />
    </>;
}